import { getEncumbranceInstitutionName } from '@core/util/encumbrance';
import { centsToDollars } from '@core/util/money';
import {
	getMortgageLenderNamesForTitleInsurance,
	getMortgageLenders,
	getMortgagePrimaryLender,
} from '@core/util/mortgage';
import { BusinessUser, BusinessUserRole } from '@models/businesses';
import {
	AssociatedContactType,
	ClientType,
	PersonClient,
} from '@models/clients';
import { Encumbrance } from '@models/encumbrance';
import { LawFirm } from '@models/lawFirms';
import {
	ActingForType,
	BusinessMatterClientWithAssociations,
	CondominiumType,
	MatterBusinessUser,
	MatterClientAssociations,
	MatterFileType,
	MatterPropertyDetails,
	MatterPropertyType,
	MatterTitle,
	PersonMatterClientWithAssociations,
	PropertyStatus,
	PropertyType,
	PropertyUnitType,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import {
	MatterMortgagePriority,
	MatterMortgageWithAssociations,
	TransactionType,
} from '@models/mortgage';
import { LenderWithAssociations, MortgageeType } from '@models/lender';
import {
	TitlePlusActingFor,
	TitlePlusAddress,
	TitlePlusFiduciary,
	TitlePlusFiduciaryType,
	TitlePlusLawFirm,
	TitlePlusLawyerClerk,
	TitlePlusMatter,
	TitlePlusMortgage,
	TitlePlusMortgagee,
	TitlePlusMortgageeType,
	TitlePlusNumberOfUnits,
	TitlePlusPIN,
	TitlePlusPolicyType,
	TitlePlusProperty,
	TitlePlusPropertyType,
	TitlePlusPurchaser,
	TitlePlusTransactionType,
} from '@models/title-insurance/title-plus';
import { UserProfile } from '@models/userProfile';

const propertyId = '123';

export const buildMatter = (
	matterNumber: string,
	closingDate: string,
	propertyDetails: MatterPropertyDetails,
	assignedLawyer: MatterBusinessUser,
	assignedClerk: MatterBusinessUser,
	allUsers: BusinessUser[],
	businessName: string,
	otherPartyFirm: LawFirm,
	fileType: MatterFileType,
	mortgages: MatterMortgageWithAssociations[],
	purchasePrice: number,
	matterTitles: MatterTitle[],
	purchasers: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
	vendors: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
	remains: Encumbrance[],
	propertyValue: number,
	user: UserProfile,
	actingFor: ActingForType,
): TitlePlusMatter => {
	const matter: TitlePlusMatter = {
		matterNumber,
		fileNumber: matterNumber,
		closingDate,
		provinceCode: propertyDetails?.address?.province,
		lawyer: buildLawyerClerk(
			getBusinessUser(assignedLawyer.id, allUsers),
			businessName,
			user,
		),
		properties: [
			buildProperty(
				propertyDetails,
				fileType,
				mortgages,
				purchasePrice,
				matterTitles,
				propertyValue,
				actingFor,
			),
		],
		purchasers: getPurchasers(purchasers),
	};

	if (otherPartyFirm) {
		matter.lawFirm = buildLawFirm(otherPartyFirm);
	}

	const clerk = getBusinessUser(assignedClerk?.id, allUsers);

	if (clerk) {
		matter.clerk = buildLawyerClerk(clerk, businessName);
	}

	if (mortgages || remains) {
		matter.mortgages = getMortgages(mortgages, remains, closingDate);
	}

	if (vendors && vendors.length > 0) {
		matter.vendors = getPurchasers(vendors);
	}

	return matter;
};

const getBusinessUser = (
	userId: string,
	allUsers: BusinessUser[],
): BusinessUser => allUsers.find((user) => user.id === userId);

const buildLawyerClerk = (
	_lawyerClerk: BusinessUser,
	businessName: string,
	user?: UserProfile,
): TitlePlusLawyerClerk => {
	const phone =
		_lawyerClerk?.role === BusinessUserRole.CLERK
			? _lawyerClerk?.phoneNumber
			: user?.phoneNumber;
	const lawyerClerk: TitlePlusLawyerClerk = {
		firstName: _lawyerClerk.firstName,
		lastName: _lawyerClerk.lastName,
		firmName: businessName,
		homePhone: phone,
		workPhone: phone,
		mobilePhone: phone,
		fax: _lawyerClerk.faxNumber,
		email: _lawyerClerk.email,
	};

	if (_lawyerClerk?.role === BusinessUserRole.LAWYER) {
		lawyerClerk.lsn = _lawyerClerk.lsn;
	}

	return lawyerClerk;
};

const buildLawFirm = (lawFirm: LawFirm): TitlePlusLawFirm => {
	const address: TitlePlusAddress = {
		type: 'Residence',
		line1: lawFirm?.address?.street,
		line2: lawFirm?.address?.unit,
		city: lawFirm?.address?.city,
		province: lawFirm?.address?.province,
		postalCode: lawFirm?.address?.postcode,
		country: lawFirm?.address?.country,
	};
	const firm: TitlePlusLawFirm = {
		name: lawFirm?.name,
		workPhone: lawFirm?.phoneNumber,
		mobilePhone: lawFirm?.phoneNumber,
		fax: lawFirm?.faxNumber,
		email: lawFirm?.email,
		addresses: [address],
	};

	return firm;
};

const buildProperty = (
	propertyDetails: MatterPropertyDetails,
	fileType: MatterFileType,
	mortgages: MatterMortgageWithAssociations[],
	purchasePrice: number, //use for purchase
	matterTitles: MatterTitle[],
	propertyValue: number, //use for refi
	actinFor: ActingForType,
): TitlePlusProperty => {
	const address: TitlePlusAddress = {
		type: 'Residence',
		line1: propertyDetails.address.street,
		line2: propertyDetails.address.unit,
		city: propertyDetails.address.city,
		province: propertyDetails.address.province,
		postalCode: propertyDetails.address.postcode,
		country: propertyDetails.address.country,
	};

	const property: TitlePlusProperty = {
		address,
		propertyId,
		type: propertyMapping(propertyDetails.propertyType),
		PolicyType: getPolicyType(fileType, mortgages),
		TransactionType: getTransactionType(propertyDetails, fileType),
		isParcelOfTiedLand: !!propertyDetails?.propertyType?.parcelOfTiedLand,
		legalDescription: getLegalDescription(matterTitles),
		PINs: getPINs(matterTitles),
		rollNumber: propertyDetails?.rollNumber,
		landRegistryOffice: {
			name: propertyDetails?.lro?.selected?.name,
			officeNo: `LRO ${propertyDetails?.lro?.selected?.id}`,
		},
		numberOfUnits: getNumberOfUnits(propertyDetails.propertyType),
		actingFor: getActingFor(actinFor, mortgages, fileType),
	};

	if (fileType === MatterFileType.PURCHASE) {
		property.condition =
			propertyDetails?.propertyType?.status === PropertyStatus.NEW
				? 'New'
				: 'Resale';
		property.purchasePrice = Number(centsToDollars(purchasePrice));
	} else if (fileType === MatterFileType.REFINANCE) {
		property.marketValue = Number(centsToDollars(propertyValue));
	}

	if (property.isParcelOfTiedLand) {
		property.commonElementsCondoNumber =
			propertyDetails?.propertyType?.condoPlan;
	}

	return property;
};

const getActingFor = (
	actingFor: ActingForType,
	mortgages: MatterMortgageWithAssociations[],
	fileType: MatterFileType,
): TitlePlusActingFor => {
	if (fileType === MatterFileType.PURCHASE) {
		if (
			mortgages.find(
				(mortgage) =>
					mortgage.actingForMortgagee &&
					mortgage.priority === MatterMortgagePriority.PRIORITY_1,
			)
		) {
			return 'purchaser_and_lender';
		} else {
			return 'purchaser';
		}
	} else if (fileType === MatterFileType.REFINANCE) {
		switch (actingFor) {
			case ActingForType.MORTGAGEE:
				return 'lender';
			case ActingForType.MORTGAGOR:
				if (
					mortgages.find(
						(mortgage) =>
							mortgage.actingForMortgagee &&
							mortgage.priority === MatterMortgagePriority.PRIORITY_1,
					)
				) {
					return 'purchaser_and_lender';
				} else {
					return 'purchaser';
				}
			case ActingForType.BOTH_PRIMARY_MORTGAGEE:
			case ActingForType.BOTH_PRIMARY_MORTGAGOR:
				return 'purchaser_and_lender';
		}
	}
};

const getNumberOfUnits = (
	propertyType: MatterPropertyType,
): TitlePlusNumberOfUnits => {
	if (propertyType.multiUnit) {
		switch (propertyType.numberOfUnits) {
			case 2:
				return '2';
			case 3:
				return '3';
			case 4:
				return '4';
			case 5:
				return '5';
			case 6:
				return '6';
			default:
				return 'more_than_6';
		}
	}
};

const propertyMapping = (
	propertyType: MatterPropertyType,
): TitlePlusPropertyType => {
	if (propertyType.usage === PropertyUsage.COMMERCIAL) {
		return 'Commercial';
	} else if (propertyType.usage === PropertyUsage.RESIDENTIAL) {
		switch (propertyType.type) {
			case PropertyType.HOME:
				if (propertyType.multiUnit) {
					if (propertyType.unitType === PropertyUnitType.LIVE_WORK) {
						return 'Commercial';
					} else {
						return 'MultiUnit';
					}
				} else {
					return 'SingleFamily';
				}
			case PropertyType.CONDO:
				if (
					[CondominiumType.STANDARD, CondominiumType.PHASED].includes(
						propertyType.condoType,
					)
				) {
					return 'Condominium';
				} else if (propertyType.condoType === CondominiumType.VACANT) {
					return 'VacantLandCondo';
				} else if (propertyType.condoType === CondominiumType.LEASEHOLD) {
					return 'Leasehold';
				}
				break;
			case PropertyType.VACANT:
				return 'VacantLand';
			case PropertyType.FARM_LAND:
				return 'Farm';
		}
	}
};

const getPolicyType = (
	fileType: MatterFileType,
	mortgages: MatterMortgageWithAssociations[],
): TitlePlusPolicyType => {
	const mortgageIsMMS: boolean = mortgages.some((mortgage) => mortgage.mms);
	if (mortgageIsMMS) {
		return 'owner_policy_only';
	}

	if (fileType === MatterFileType.REFINANCE) {
		return 'lender_policy_only';
	} else if (fileType === MatterFileType.PURCHASE) {
		if (mortgages.length === 0) {
			return 'owner_policy_only';
		} else {
			return 'owner_and_lender';
		}
	}
};

const getTransactionType = (
	propertyDetails: MatterPropertyDetails,
	fileType: MatterFileType,
): TitlePlusTransactionType => {
	if (fileType === MatterFileType.PURCHASE) {
		if (propertyDetails?.propertyType?.status === PropertyStatus.RESALE) {
			return 'Resale';
		} else if (propertyDetails?.propertyType?.status === PropertyStatus.NEW) {
			return 'New';
		}
	} else if (fileType === MatterFileType.REFINANCE) {
		return 'MortgageOnly';
	}
};

const getLegalDescription = (matterTitles: MatterTitle[]): string => {
	const sortedTitles = matterTitles.sort((a, b) => {
		if (a.primary) {
			return -1;
		} else if (b.primary) {
			return 1;
		} else {
			return 0;
		}
	});

	const legalDescription = sortedTitles
		.map((title) => title.legalDescription)
		.join(', ');

	return legalDescription.substring(0, 5000);
};

const getPINs = (matterTitles: MatterTitle[]): TitlePlusPIN[] => {
	const pins: TitlePlusPIN[] = [];

	matterTitles.forEach((title) => {
		const pin: TitlePlusPIN = {
			pin: title.pinNumber,
		};

		pins.push(pin);
	});

	return pins;
};

const getPurchasers = (
	purchasers: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
): TitlePlusPurchaser[] => {
	const titlePlusPurchasers: TitlePlusPurchaser[] = purchasers.map(
		(purchaser) => {
			const fiduciaries = getFiduciaries(purchaser.clientAssociations);

			const _purchaser: TitlePlusPurchaser = {
				Id: purchaser.id,
				propertyId,
				gender: (purchaser as PersonMatterClientWithAssociations).gender,
				dateOfBirth: (purchaser as PersonMatterClientWithAssociations)
					.dateOfBirth,
				fiduciaries,
			};

			if (purchaser.type === ClientType.CORPORATION) {
				_purchaser.name = (
					purchaser as BusinessMatterClientWithAssociations
				).name;
			} else {
				_purchaser.firstName = (
					purchaser as PersonMatterClientWithAssociations
				).firstName;
				_purchaser.lastName = (
					purchaser as PersonMatterClientWithAssociations
				).lastName;
				_purchaser.middleName = (
					purchaser as PersonMatterClientWithAssociations
				).middleNames;
			}

			return _purchaser;
		},
	);

	return titlePlusPurchasers;
};

const getFiduciaryType = (
	association: MatterClientAssociations,
): TitlePlusFiduciaryType => {
	switch (Object.keys(association?.associations.types)[0]) {
		case AssociatedContactType.POWER_OF_ATTORNEY:
			return 'PowerOfAttorney';
		case AssociatedContactType.SIGNING_OFFICER:
			return 'SigningOfficer';
		case AssociatedContactType.ESTATE_TRUSTEE:
			return 'Executer';
	}
};

const getFiduciaries = (
	associations: MatterClientAssociations[],
): TitlePlusFiduciary[] => {
	if (associations && associations.length > 0) {
		const fiduciaries = associations.reduce((acc, association) => {
			const type = getFiduciaryType(association);
			if (!type) {
				return acc;
			}

			const fiduciary: TitlePlusFiduciary = {
				Id: association?.client?.id,
				type,
			};

			if (association?.client?.type === ClientType.CORPORATION) {
				fiduciary.name = (
					association?.client as BusinessMatterClientWithAssociations
				).name;
			} else {
				fiduciary.title = (association?.client as PersonClient)?.title;
				fiduciary.firstName = (
					association?.client as PersonMatterClientWithAssociations
				).firstName;
				fiduciary.lastName = (
					association?.client as PersonMatterClientWithAssociations
				).lastName;
				fiduciary.middleName = (
					association?.client as PersonMatterClientWithAssociations
				).middleNames;
			}

			return acc.concat(fiduciary);
		}, [] as TitlePlusFiduciary[]);

		return fiduciaries;
	}

	return [];
};

const getLenderAddress = (lender: LenderWithAssociations): TitlePlusAddress => {
	const branchAddress = lender?.branch?.address;
	const lenderAddress = lender?.address;
	if (lender?.mortgageeType === MortgageeType.COMPUTERSHARE) {
		return {
			type: 'Business',
			line1: branchAddress?.street || lenderAddress?.street,
			line2: branchAddress?.unit || lenderAddress?.unit,
			city: branchAddress?.city || lenderAddress?.city,
			province: branchAddress?.province || lenderAddress?.province,
			postalCode: branchAddress?.postcode || lenderAddress?.postcode,
			country: branchAddress?.country || lenderAddress?.country,
		};
	}

	return {
		type: 'Business',
		line1: lenderAddress?.street,
		line2: lenderAddress?.unit,
		city: lenderAddress?.city,
		province: lenderAddress?.province,
		postalCode: lenderAddress?.postcode,
		country: lenderAddress?.country,
	};
};

const getMortgages = (
	mortgages: MatterMortgageWithAssociations[],
	remain: Encumbrance[],
	closingDate: string,
): TitlePlusMortgage[] => {
	const _mortgages: TitlePlusMortgage[] = mortgages.map((mortgage) => {
		const AllLendersNames = getMortgageLenderNamesForTitleInsurance(mortgage);
		const lender = getMortgagePrimaryLender(mortgage) as LenderWithAssociations;
		const address = getLenderAddress(lender);

		const mortgagee: TitlePlusMortgagee = {
			type: getMortgageeType(mortgage),
			name: AllLendersNames ? AllLendersNames : '',
			workPhone: lender?.phone,
			fax: lender?.fax,
			email: lender?.email,
			addresses: [address],
		};

		const _mortgage: TitlePlusMortgage = {
			Id: mortgage.id,
			propertyId: propertyId,
			referenceNumber: mortgage.referenceNumber,
			registeredAmount: Number(centsToDollars(mortgage.amount)),
			priority: mortgage.priority,
			mortgagees: [mortgagee],
			isExistingMortgage: false,
			mortgageAdvanceDate: closingDate,
			actingForLender: mortgage.actingForMortgagee,
		};

		return _mortgage;
	});

	const _remains: TitlePlusMortgage[] = remain.map((remain) => {
		const lender =
			remain.mortgageeType === MortgageeType.PRIVATE_LENDER
				? remain.privateLender
				: remain.financialInstitution;
		const type =
			remain.mortgageeType === MortgageeType.PRIVATE_LENDER
				? 'Private'
				: 'Institutional';
		const address: TitlePlusAddress = {
			type: 'Business',
			line1: lender?.address?.street,
			line2: lender?.address?.unit,
			city: lender?.address?.city,
			province: lender?.address?.province,
			postalCode: lender?.address?.postcode,
			country: lender?.address?.country,
		};

		const mortgagee: TitlePlusMortgagee = {
			type,
			name: getEncumbranceInstitutionName(remain),
			workPhone: lender?.phone,
			fax: lender?.fax,
			email: lender?.email,
			addresses: [address],
		};

		const _mortgage: TitlePlusMortgage = {
			Id: remain.id,
			propertyId: propertyId,
			priority: remain.priorityAfterClosing,
			mortgagees: [mortgagee],
			isExistingMortgage: true,
			instrumentNumber: remain.registrationNumber,
		};

		return _mortgage;
	});

	return [..._mortgages, ..._remains];
};

const getMortgageeType = (
	mortgage: MatterMortgageWithAssociations,
): TitlePlusMortgageeType => {
	const lenders = getMortgageLenders(mortgage);

	if (mortgage.transactionType === TransactionType.VTB) {
		return 'VendorTakeBack';
	}

	if (
		lenders.some(
			(mortgage) => mortgage.mortgageeType === MortgageeType.PRIVATE_LENDER,
		)
	) {
		return 'Private';
	}

	return 'Institutional';
};
